import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Brain, Clock, Target, Zap, AlertCircle, CheckCircle2, TrendingUp, Focus } from 'lucide-react';
const ObjectionHandlingSection = () => {
  const {
    isMobile
  } = useBreakpoint();
  return <SectionLayout backgroundColor="bg-gradient-to-br from-white via-[#FEE6D8]/5 to-white" paddingY="py-16 md:py-20 lg:py-24" containerWidth="wide" backgroundPattern="dots" patternColor="#003a70" patternOpacity={2}>
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          
          
          <h2 className={cn("font-bold text-[#003a70] leading-tight max-w-4xl mx-auto", isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl")}>
            The Truth About 
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              "I Can Do This Myself"
            </span>
          </h2>
        </div>

        {/* I Know What You're Thinking */}
        <div className="space-y-8 animate-fade-in" style={{
        animationDelay: '0.2s'
      }}>
          <div className="max-w-4xl mx-auto">
            <p className={cn("text-[#003a70]/90 leading-relaxed mb-8", isMobile ? "text-lg" : "text-xl lg:text-2xl")}>
              I know what you're thinking.
            </p>
            
            {/* Thinking Bubble */}
            <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/60 shadow-xl relative overflow-hidden">
              {/* Thought accent */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#003a70] to-[#003a70]/80"></div>
              
              <div className="space-y-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-16 h-16 bg-[#003a70] rounded-full flex items-center justify-center shadow-lg">
                    <Brain className="text-white" size={28} />
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-6 bg-white/60 rounded-xl border border-[#FEE6D8]/50">
                  <div className="text-2xl mt-1">👉</div>
                  <p className={cn("text-[#003a70] font-bold flex-1 leading-relaxed", isMobile ? "text-lg" : "text-xl lg:text-2xl")}>
                    "I'm smart. I'm capable. I can create this stuff myself."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Acknowledgment */}
        <div className="space-y-6 animate-fade-in" style={{
        animationDelay: '0.4s'
      }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-2xl p-8 md:p-10 border border-[#FEE6D8]/60 shadow-lg max-w-4xl mx-auto">
            <div className="text-center">
              <CheckCircle2 className="text-[#003a70] mx-auto mb-4" size={48} />
              <p className={cn("text-[#003a70] font-bold leading-relaxed", isMobile ? "text-xl" : "text-2xl lg:text-3xl")}>
                And you know what? You probably could.
              </p>
            </div>
          </div>
        </div>

        {/* The Real Question */}
        <div className="space-y-8 animate-fade-in" style={{
        animationDelay: '0.6s'
      }}>
          <div className="max-w-4xl mx-auto">
            <p className={cn("text-[#003a70]/90 leading-relaxed mb-8", isMobile ? "text-lg" : "text-xl lg:text-2xl")}>
              But here's the question that matters:
            </p>
            
            {/* The Real Question Highlight */}
            <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/60 shadow-xl relative overflow-hidden">
              {/* Question accent */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#003a70] to-[#FEE6D8]"></div>
              
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                    <AlertCircle className="text-white" size={32} />
                  </div>
                </div>
                
                <p className={cn("text-[#003a70] font-bold leading-tight", isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl")}>
                  Will you?
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Zone of Genius Truth */}
        <div className="space-y-8 animate-fade-in" style={{
        animationDelay: '0.8s'
      }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/30 via-white to-[#003a70]/10 rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/60 shadow-2xl max-w-5xl mx-auto relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-4 right-4 opacity-10">
              <Target className="text-[#003a70]" size={48} />
            </div>
            <div className="absolute bottom-4 left-4 opacity-10">
              <Focus className="text-[#003a70]" size={48} />
            </div>
            
            <div className="relative z-10 text-center space-y-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                  <TrendingUp className="text-white" size={28} />
                </div>
              </div>
              
              <p className={cn("text-[#003a70] font-bold leading-relaxed", isMobile ? "text-lg md:text-xl" : "text-xl lg:text-2xl")}>
                The people who succeed focus on their zone of genius and get everything else done for them.
              </p>
            </div>
          </div>
        </div>

        {/* Your Zone of Genius */}
        <div className="space-y-8 animate-fade-in" style={{
        animationDelay: '1.0s'
      }}>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* Zone of Genius */}
              <div className="bg-gradient-to-br from-[#FEE6D8]/40 to-[#FEE6D8]/20 rounded-2xl p-8 border-2 border-[#FEE6D8]/60 shadow-lg">
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <Zap className="text-white" size={28} />
                  </div>
                  
                  <div>
                    <h3 className={cn("font-bold text-[#003a70] mb-4", isMobile ? "text-lg" : "text-xl lg:text-2xl")}>
                      Your Zone of Genius IS:
                    </h3>
                    
                    <div className="space-y-3 text-left">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="text-[#003a70] flex-shrink-0" size={20} />
                        <p className={cn("text-[#003a70] font-medium", isMobile ? "text-base" : "text-lg")}>
                          Selling AI business cards
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="text-[#003a70] flex-shrink-0" size={20} />
                        <p className={cn("text-[#003a70] font-medium", isMobile ? "text-base" : "text-lg")}>
                          Building client relationships
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* NOT Your Zone */}
              <div className="bg-gradient-to-br from-[#003a70]/10 to-[#003a70]/5 rounded-2xl p-8 border-2 border-[#003a70]/20 shadow-lg">
                <div className="text-center space-y-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center mx-auto shadow-lg">
                    <Clock className="text-white" size={28} />
                  </div>
                  
                  <div>
                    <h3 className={cn("font-bold text-[#003a70] mb-4", isMobile ? "text-lg" : "text-xl lg:text-2xl")}>
                      Your Zone of Genius is NOT:
                    </h3>
                    
                    <div className="space-y-3 text-left">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="text-[#003a70] flex-shrink-0" size={20} />
                        <p className={cn("text-[#003a70] font-medium", isMobile ? "text-base" : "text-lg")}>
                          Spending 50+ hours creating marketing materials
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>;
};
export default ObjectionHandlingSection;